import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import type { CalculoDeFila, Ciclo, PlanillaRegistrada, RevisionDePlanilla } from './contrato';

type Filtro = 'todas' | 'fuera' | 'avisos';

export function CargaDePesajes({
  ciclo,
  alCerrar,
  alRegistrar,
}: {
  ciclo: Ciclo;
  alCerrar: () => void;
  alRegistrar: () => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [fichero, setFichero] = useState<File | null>(null);
  const [encima, setEncima] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [bajando, setBajando] = useState(false);

  const base = `/generadores/ciclos/${ciclo.id}/pesajes`;

  const revisar = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);

      return api.subir<RevisionDePlanilla>(`${base}/importacion/revision`, cuerpo);
    },
    onSuccess: (r) => setFiltro(r.rechazadas > 0 && r.validas === 0 ? 'fuera' : 'todas'),
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO LEER LA PLANILLA'),
  });

  const registrar = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      return api.subir<PlanillaRegistrada>(`${base}/importacion`, cuerpo);
    },
    onSuccess: (r) => {
      notificar.exito(
        `${r.registrados} PESAJES · $${r.total.toFixed(2)}`,
        r.rechazados > 0 ? `${r.rechazados} filas quedaron fuera` : 'Planilla registrada',
      );
      alRegistrar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REGISTRAR'),
  });

  function elegir(archivo: File | undefined) {
    if (!archivo) {
      return;
    }
    setFichero(archivo);
    revisar.reset();
    revisar.mutate(archivo);
  }

  async function descargarPlantilla() {
    setBajando(true);
    try {
      const excel = await api.descargar(`${base}/plantilla.xlsx`);
      if (!excel) {
        notificar.error('NO SE PUDO ARMAR LA PLANTILLA');
        return;
      }
      const url = URL.createObjectURL(excel);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `pesajes-${ciclo.periodo}.xlsx`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR');
    } finally {
      setBajando(false);
    }
  }

  const revision = revisar.data;
  const filas =
    revision?.filas.filter((f) =>
      filtro === 'fuera'
        ? f.motivos.length > 0
        : filtro === 'avisos'
          ? f.motivos.length === 0 && (f.avisos?.length ?? 0) > 0
          : true,
    ) ?? [];
  const conAvisos = revision?.conAvisos ?? 0;
  const ausentes = revision?.ausentes ?? [];

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Cargar desde Excel"
      subtitulo={`Los pesajes de ${ciclo.periodo}`}
      icono={FileSpreadsheet}
      ancho="amplio"
    >
      <div className="grid gap-4">
        <div
          onDragOver={(evento) => {
            evento.preventDefault();
            setEncima(true);
          }}
          onDragLeave={() => setEncima(false)}
          onDrop={(evento) => {
            evento.preventDefault();
            setEncima(false);
            elegir(evento.dataTransfer.files?.[0]);
          }}
          className={`rounded-2xl border-2 border-dashed p-6 text-center transition ${
            encima ? 'border-marca-400 bg-marca-50/50' : 'border-armazon-200 bg-white'
          }`}
        >
          <input
            ref={entrada}
            type="file"
            accept=".xlsx"
            className="hidden"
            aria-label="Planilla de pesajes"
            onChange={(evento) => {
              elegir(evento.target.files?.[0]);
              evento.target.value = '';
            }}
          />
          <Upload className="mx-auto mb-2 size-7 text-armazon-300" aria-hidden />
          <p className="text-sm font-semibold uppercase tracking-wide text-armazon-600">
            {fichero ? fichero.name : 'Arrastre aquí la planilla .xlsx'}
          </p>
          <p className="mt-1 text-[11px] uppercase text-armazon-400">
            Primero se revisa: no se registra nada hasta que usted lo confirme
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => entrada.current?.click()}
              disabled={revisar.isPending || registrar.isPending}
              className={BOTON_SECUNDARIO}
            >
              {revisar.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Upload className="size-4" aria-hidden />
              )}
              {fichero ? 'Otro archivo' : 'Elegir archivo'}
            </button>
            <button
              type="button"
              onClick={() => void descargarPlantilla()}
              disabled={bajando}
              className={BOTON_SECUNDARIO}
            >
              {bajando ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Download className="size-4" aria-hidden />
              )}
              Plantilla del mes
            </button>
          </div>
        </div>

        {revision && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Resumen valor={String(revision.validas)} rotulo="Entran" tono="bien" />
              <Resumen valor={String(revision.rechazadas)} rotulo="Quedan fuera" tono="mal" />
              <Resumen valor={String(conAvisos)} rotulo="Con avisos" tono="aviso" />
              <Resumen valor={`$${revision.total.toFixed(2)}`} rotulo="Total" tono="neutro" />
              <div
                role="group"
                aria-label="Qué filas ver"
                className="ml-auto inline-flex overflow-hidden rounded-lg border border-armazon-200"
              >
                {(
                  [
                    ['todas', 'Todas'],
                    ['avisos', `Con avisos (${conAvisos})`],
                    ['fuera', `Fuera (${revision.rechazadas})`],
                  ] as const
                ).map(([clave, rotulo]) => (
                  <button
                    key={clave}
                    type="button"
                    onClick={() => setFiltro(clave)}
                    aria-pressed={filtro === clave}
                    className={`h-8 px-3 text-[10px] font-bold uppercase ${
                      filtro === clave
                        ? 'bg-marca-600 text-white'
                        : 'bg-white text-armazon-600 hover:bg-armazon-50'
                    }`}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-[45dvh] overflow-auto rounded-xl border border-armazon-200">
              <table className="w-full min-w-[880px] text-left text-xs">
                <thead className="sticky top-0 bg-armazon-50 text-[10px] uppercase tracking-wide text-armazon-500">
                  <tr>
                    <th className="px-3 py-2">Fila</th>
                    <th className="px-3 py-2">Generador</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2 text-right">Peso</th>
                    <th className="px-3 py-2">Guía</th>
                    <th className="px-3 py-2">Cálculo</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-armazon-100">
                  {filas.map((f) => {
                    const entra = f.motivos.length === 0;
                    const avisos = entra ? (f.avisos ?? []) : [];
                    return (
                      <tr
                        key={f.numero}
                        className={
                          !entra ? 'bg-rose-50/60' : avisos.length > 0 ? 'bg-amber-50/60' : ''
                        }
                      >
                        <td className="importe px-3 py-2 text-armazon-400">{f.numero}</td>
                        <td className="px-3 py-2">
                          <span className="block font-semibold uppercase text-armazon-800">
                            {f.generador ?? '—'}
                          </span>
                          <span className="importe block text-[10px] text-armazon-400">
                            {f.ruc ?? 'SIN RUC'}
                          </span>
                          {f.calculo && (
                            <span className="block text-[10px] uppercase text-armazon-500">
                              {f.calculo.categoria}
                            </span>
                          )}
                          {!entra && (
                            <span className="mt-1 block text-[10px] font-semibold uppercase text-rose-700">
                              {f.motivos.join(' · ')}
                            </span>
                          )}
                          {avisos.map((aviso) => (
                            <span
                              key={aviso}
                              className="mt-1 flex items-start gap-1 text-[10px] font-semibold uppercase text-amber-800"
                            >
                              <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden />
                              {aviso}
                            </span>
                          ))}
                        </td>
                        <td className="importe px-3 py-2 text-armazon-600">{f.fecha ?? '—'}</td>
                        <td className="importe px-3 py-2 text-right">
                          {f.kilos != null ? `${f.kilos.toLocaleString('es-EC')} KG` : '—'}
                        </td>
                        <td className="importe px-3 py-2 text-armazon-500">{f.guia ?? '—'}</td>
                        <td className="px-3 py-2">{f.calculo ? <Calculo c={f.calculo} /> : '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {entra ? (
                            <span className="importe inline-flex items-center gap-1 font-bold text-armazon-900">
                              <CheckCircle2 className="size-3.5 text-emerald-600" aria-hidden />
                              {`$${f.total?.toFixed(2)}`}
                            </span>
                          ) : (
                            <XCircle
                              className="ml-auto size-4 text-rose-500"
                              aria-label="Queda fuera"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filas.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center uppercase text-armazon-400">
                        {filtro === 'todas'
                          ? 'La planilla no trae filas'
                          : 'Ninguna fila con ese filtro'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {ausentes.length > 0 && (
              <details className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <summary className="cursor-pointer text-[11px] font-bold uppercase text-amber-900">
                  {ausentes.length === 1
                    ? '1 generador activo no viene en la planilla ni tiene pesaje este mes'
                    : `${ausentes.length} generadores activos no vienen en la planilla ni tienen pesaje este mes`}
                </summary>
                <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                  {ausentes.map((a) => (
                    <li key={a.generadorId} className="text-[10px] uppercase text-amber-900">
                      <span className="font-semibold">{a.nombre}</span>
                      <span className="importe text-amber-700"> · {a.identificacion}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={alCerrar} className={BOTON_SECUNDARIO}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => fichero && registrar.mutate(fichero)}
            disabled={!fichero || !revision || revision.validas === 0 || registrar.isPending}
            className={BOTON_PRINCIPAL}
          >
            {registrar.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 className="size-4" aria-hidden />
            )}
            {revision ? `Registrar ${revision.validas} pesajes` : 'Registrar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Calculo({ c }: { c: CalculoDeFila }) {
  const cantidad = c.cantidad.toLocaleString('es-EC', { maximumFractionDigits: 3 });
  return (
    <span className="importe block text-[10px] leading-relaxed text-armazon-600">
      <span className="block">
        {cantidad} {c.unidad} × ${c.tarifaUnitaria.toFixed(2)} = ${c.servicio.toFixed(2)}
      </span>
      {c.recargo > 0 && (
        <span className="block">
          + {c.kmExcedentes.toLocaleString('es-EC')} KM = ${c.recargo.toFixed(2)}
        </span>
      )}
    </span>
  );
}

function Resumen({
  valor,
  rotulo,
  tono,
}: {
  valor: string;
  rotulo: string;
  tono: 'bien' | 'mal' | 'aviso' | 'neutro';
}) {
  const color =
    tono === 'bien'
      ? 'text-emerald-700'
      : tono === 'mal'
        ? 'text-rose-700'
        : tono === 'aviso'
          ? 'text-amber-700'
          : 'text-marca-800';
  return (
    <div className="rounded-xl border border-armazon-200 bg-white px-4 py-2">
      <span className={`importe block text-lg font-bold ${color}`}>{valor}</span>
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </span>
    </div>
  );
}

const BOTON_PRINCIPAL =
  'inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50';
const BOTON_SECUNDARIO =
  'inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50 disabled:opacity-50';
